import { useEffect, useState, useCallback } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { SectionHead, BandBadge, StatusBadge, ScoreRing, Val, isVal, NA } from "./VdaShared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Search, Phone, Globe, ExternalLink, MapPin, X, UserPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Discovered", "Verified", "Contacted", "Onboarded", "Rejected"];

export default function VdaProspects() {
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ city: "", category: "", band: "", has_phone: false, has_website: false });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => { papi.get("/vda/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setRows(null);
    const params = {};
    if (f.city) params.city = f.city;
    if (f.category) params.category = f.category;
    if (f.band) params.band = f.band;
    if (f.has_phone) params.has_phone = true;
    if (f.has_website) params.has_website = true;
    try { const { data } = await papi.get("/vda/prospects", { params }); setRows(data); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); setRows([]); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((r) => !q || r.business_name?.toLowerCase().includes(q.toLowerCase()));

  const updateStatus = async (vid, status) => {
    try { await papi.put(`/vda/prospects/${vid}/status`, { verification_status: status });
      setRows((rs) => rs.map((r) => r.id === vid ? { ...r, verification_status: status } : r));
      if (sel?.id === vid) setSel((s) => ({ ...s, verification_status: status }));
      toast.success("Status updated");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onboard = async () => {
    if (!sel) return;
    setOnboarding(true);
    try {
      const { data } = await papi.post(`/vda/prospects/${sel.id}/onboard`);
      const patch = { verification_status: "Onboarded", onboarded_vendor_id: data.vendor_id };
      setRows((rs) => rs.map((r) => r.id === sel.id ? { ...r, ...patch } : r));
      setSel((s) => ({ ...s, ...patch }));
      toast.success(data.already ? "Already in Partner CRM" : `Onboarded "${data.vendor_name}" to Partner CRM (pending review)`);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setOnboarding(false); }
  };

  return (
    <div>
      <SectionHead title="Vendor Prospects" desc={`${filtered.length} prospect${filtered.length === 1 ? "" : "s"} shown`} />

      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="prospects-search" className="rounded-xl pl-9" placeholder="Search business name…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Input data-testid="prospects-filter-city" className="w-36 rounded-xl" placeholder="City" value={f.city} onChange={(e) => set("city", e.target.value)} />
        <select data-testid="prospects-filter-category" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={f.category} onChange={(e) => set("category", e.target.value)}>
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select data-testid="prospects-filter-band" className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={f.band} onChange={(e) => set("band", e.target.value)}>
          <option value="">All priorities</option>
          <option>High Priority</option><option>Medium Priority</option><option>Low Priority</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="prospects-filter-phone" type="checkbox" checked={f.has_phone} onChange={(e) => set("has_phone", e.target.checked)} /> Has phone</label>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input data-testid="prospects-filter-website" type="checkbox" checked={f.has_website} onChange={(e) => set("has_website", e.target.checked)} /> Has website</label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {rows === null ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-500" /></div>
          : filtered.length === 0 ? <div className="flex h-64 items-center justify-center text-sm text-slate-400">No prospects found. Run a discovery from “Discover Vendors”.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="prospects-table">
                <thead><tr className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3">Business</th><th className="px-4 py-3">City</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Website</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3 text-center">Score</th><th className="px-4 py-3 text-center">Complete</th><th className="px-4 py-3">Status</th></tr></thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} data-testid={`prospect-row-${r.id}`} onClick={() => setSel(r)} className="cursor-pointer border-b border-slate-50 hover:bg-teal-50/40">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.business_name}<div className="text-xs font-normal text-slate-400">{r.category}</div></td>
                      <td className="px-4 py-3 text-slate-600">{r.city}</td>
                      <td className="px-4 py-3">{isVal(r.phone) ? <span className="inline-flex items-center gap-1 text-slate-700"><Phone className="h-3.5 w-3.5 text-teal-500" />{r.phone}</span> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3">{isVal(r.website) ? <Globe className="h-4 w-4 text-sky-500" /> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3"><BandBadge band={r.score_band} /></td>
                      <td className="px-4 py-3 text-center font-bold tabular-nums text-slate-700">{r.discovery_score}</td>
                      <td className="px-4 py-3 text-center tabular-nums text-slate-600">{r.profile_completeness}%</td>
                      <td className="px-4 py-3"><StatusBadge status={r.verification_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {sel && (
            <>
              <SheetHeader><SheetTitle className="pr-8 text-left font-display text-xl">{sel.business_name}</SheetTitle>
                <SheetDescription className="text-left">{sel.category} · {sel.city}{sel.state ? ", " + sel.state : ""} · sourced from public Google listing</SheetDescription>
              </SheetHeader>
              <div className="mt-4 flex items-center gap-4">
                <ScoreRing value={sel.discovery_score} size={54} />
                <div><BandBadge band={sel.score_band} /><p className="mt-1 text-xs text-slate-400">Profile {sel.profile_completeness}% complete</p></div>
                <div className="ml-auto"><StatusBadge status={sel.verification_status} /></div>
              </div>

              <div className="mt-6 space-y-3 text-sm">
                <Row label="Category" v={`${sel.category}${isVal(sel.subcategory) ? " · " + sel.subcategory : ""}`} />
                <Row label="City / State" v={`${sel.city}${sel.state ? ", " + sel.state : ""}`} />
                <Row label="Address" v={sel.address} icon={MapPin} />
                <Row label="Phone" v={sel.phone} />
                <Row label="Website" v={sel.website} href />
                <Row label="Email" v={sel.email} />
                <Row label="Instagram" v={sel.instagram} href />
                <Row label="Services" v={sel.services} />
                <Row label="Google Rating" v={sel.rating ? `${sel.rating} ★ (${sel.rating_count || 0} reviews)` : NA} />
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Source</p>
                  {isVal(sel.google_url)
                    ? <a href={sel.google_url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-teal-600 hover:underline">Public Google Business Listing <ExternalLink className="h-3.5 w-3.5" /></a>
                    : <p className="mt-0.5 text-slate-500">{sel.source_type}</p>}
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Discovery Score Breakdown</p>
                <ul className="mt-2 space-y-1 text-sm text-slate-600">
                  {(sel.score_reasons || []).map((r, i) => <li key={i} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{r}</li>)}
                </ul>
              </div>

              <div className="mt-6">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Update Status</p>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} data-testid={`status-btn-${s}`} onClick={() => updateStatus(sel.id, s)}
                      className={`rounded-full px-3 py-1.5 text-xs font-semibold ring-1 ring-inset ${sel.verification_status === s ? "bg-teal-500 text-white ring-teal-500" : "bg-white text-slate-600 ring-slate-200 hover:ring-teal-300"}`}>{s}</button>
                  ))}
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-teal-200 bg-teal-50 p-4">
                {sel.onboarded_vendor_id ? (
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" /> Onboarded to Partner CRM (pending admin review)
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-800">Onboard to Verified Partner CRM</p>
                    <p className="mt-1 text-xs text-slate-500">Creates a vendor record (status: pending) pre-filled with this prospect’s public details — no re-typing.</p>
                    <Button data-testid="prospect-onboard" onClick={onboard} disabled={onboarding} className="mt-3 h-10 rounded-xl bg-teal-500 px-5 hover:bg-teal-600">
                      {onboarding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />} Onboard as Partner
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Row({ label, v, href, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-50 pb-2">
      <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</span>
      <span className="max-w-[60%] text-right text-slate-800"><Val v={v} href={href} /></span>
    </div>
  );
}
